import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Footer from "../components/Footer";
import PricingCard from "../components/PricingCard";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../authentication/firebase";
import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Heading from "../components/Heading";
import MainNav from "../components/navbar/MainNav";
import Header from "../styles/Header";
import { motion, useAnimation } from "framer-motion";
import { useSearchParams } from "react-router";
import { alertActions } from "../store/alertSlice";
import { CheckCircle } from "@mui/icons-material";
import CouponCodeCopy from "../components/CouponCodeCopy";
import WaitlistPopup from "../components/modals/WaitlistPopup";

export default function Subscription() {
  const [isYearly, setIsYearly] = useState(false);
  // const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState({ month: {}, year: {} });
  const [loading, setLoading] = useState(true);
  const [activeCoupon, setActiveCoupon] = useState(null);
  const [showWaitlist, setShowWaitlist] = useState(true);
  const controls = useAnimation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  // Load coupon from URL parameters
  const loadCouponFromURL = () => {
    const couponCode = searchParams.get("couponCode");
    if (couponCode) {
      setActiveCoupon({
        code: couponCode,
        name: "Welcome Offer",
        description: "Exclusive discount for new users - use this code during checkout!",
        type: "url-coupon"
      });
      
      // Show success message
      dispatch(
        alertActions.setAlert({
          title: `Coupon ${couponCode} is ready to use!`,
          messageType: "success",
        })
      );
    }
  };

  // Remove coupon from view
  const handleRemoveCoupon = () => {
    setActiveCoupon(null);
    // Remove coupon from URL
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("couponCode");
      return newParams;
    });
  };

  const getPlans = async () => {
    const productsQuery = query(
      collection(db, "products"),
      where("active", "==", true)
    );
    let fetchedProducts = [];
    try {
      const productsSnapshot = await getDocs(productsQuery);
      const promises = productsSnapshot.docs.map(async (product) => {
        const pricesRef = collection(db, "products", product.id, "prices");
        const pricesSnapshot = await getDocs(pricesRef);
        fetchedProducts.push({
          id: product.id,
          prices: pricesSnapshot.docs.map((price) => ({
            id: price.id,
            ...price.data(),
            productData: product.data(),
          })),
        });
      });
      await Promise.all(promises);
      let fetchedPrices = { month: [], year: [] };
      fetchedProducts.forEach((fetchedProduct) =>
        fetchedProduct.prices?.forEach((price) => {
          if (price?.interval) {
            if (!fetchedPrices[price.interval]) {
              fetchedPrices[price.interval] = [];
            }
            fetchedPrices[price.interval].push(price);
          }
        })
      );

      setPrices(fetchedPrices);
    } catch (e) {
      console.log("error while fetching prices", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPlans();
    loadCouponFromURL();
  }, []);

  // Toggle between monthly and yearly plans
  const handleTogglePeriod = () => {
    setIsYearly((prev) => !prev);
  };

  useEffect(() => {
    if(searchParams.get("error")){
      dispatch(
        alertActions.setAlert({
          title: "Error handling payment. Please try again.",
          messageType: "error",
        })
      );
    }
  }, [searchParams]);

  useEffect(() => {
    controls?.start({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    });
  }, [controls]);

  return (
    <>
      <Header
        title="Adopt a Wroot, Grow the Future"
        description="Every Wroot is more than a tree! it's a living companion. As a Wroot Guardian, you'll watch it grow, celebrate its milestones, and know your care is shaping the Earth's tomorrow."
      />
      
      {/* Waitlist Popup as Main Screen */}
      <WaitlistContainer>
        <WaitlistContent>
          <WaitlistHeader>
            <h1>🌱 More Trees Coming Soon!</h1>
            <p>
              We're working hard to bring you the most amazing tree adoption experience. 
              Join our waitlist to be the first to know when more trees are ready for adoption.
            </p>
            <JoinWaitlistButton onClick={() => setShowWaitlist(true)}>
              Join Waitlist
            </JoinWaitlistButton>
          </WaitlistHeader>
          
          <WaitlistPopup 
            isOpen={showWaitlist} 
            onClose={() => setShowWaitlist(false)} 
          />
        </WaitlistContent>
      </WaitlistContainer>

      {/* Hidden Subscription Plans - Keep for future use */}
      <StyledSubscriptionPlans style={{ display: 'none' }}>
        <motion.div
          className="header"
          initial={{ opacity: 0, y: 20 }}
          animate={controls}
        >
          <h1>Subscription Plans</h1>
          <p>Choose the best plan for your tree adoption journey.</p>
          
          {/* Active Coupon Section */}
          {activeCoupon && (
            <motion.div
              className="active-coupon-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <div className="coupon-header">
                <CheckCircle style={{ color: '#4caf50', marginRight: '8px' }} />
                <h3>🎉 Your Coupon is Ready!</h3>
              </div>
              <div className="coupon-card">
                <div className="coupon-info">
                  <h4>{activeCoupon.name}</h4>
                  <p>{activeCoupon.description}</p>
                  <CouponCodeCopy couponCode={activeCoupon.code} />
                </div>
                <button className="remove-coupon-btn" onClick={handleRemoveCoupon}>
                  ×
                </button>
              </div>
              <p className="coupon-instruction">
                Copy the code above and use it during checkout to get your discount!
              </p>
            </motion.div>
          )}
          
          {/* Toggle switch for plan period */}
          <ToggleContainer>
            <PeriodOption active={!isYearly}>Monthly</PeriodOption>
            <ToggleSwitch onClick={handleTogglePeriod} isYearly={isYearly}>
              <ToggleSlider isYearly={isYearly} />
            </ToggleSwitch>
            <PeriodOption active={isYearly}>
              Yearly <SaveBadge>Save 20%</SaveBadge>
            </PeriodOption>
          </ToggleContainer>
        </motion.div>

        <div className="plans-container">
          {/* {SUBSCRIPTION_PLANS.map((plan, index) => (
            <PricingCard key={plan.name} plan={plan} index={index} />
          ))} */}
          {loading ? (
            <CircularProgress style={{ marginTop: 50 }} color="inherit" />
          ) : prices[isYearly ? "year" : "month"]?.length > 0 ? (
            prices[isYearly ? "year" : "month"].map((price, index) => (
              <PricingCard
                key={price.id || index}
                priceInfo={price}
              />
            ))
          ) : (
            <NoPricesMessage>
              No {isYearly ? "yearly" : "monthly"} plans available at this time.
            </NoPricesMessage>
          )}
        </div>
      </StyledSubscriptionPlans>
    </>
  );
}

// Waitlist Container Styles
const WaitlistContainer = styled.div`
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: white;
`;

const WaitlistContent = styled.div`
  max-width: 600px;
  width: 100%;
  text-align: center;
`;

const WaitlistHeader = styled.div`
  margin-bottom: 3rem;
  
  h1 {
    font-size: 3rem;
    color: var(--text-light-color);
    margin-bottom: 1.5rem;
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    color: var(--text-gray-color);
    line-height: 1.6;
    margin: 0 0 2rem 0;
  }
`;

const JoinWaitlistButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 16px 32px;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(75, 192, 163, 0.3);
  
  &:hover {
    background-color: #2d7a5f;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(75, 192, 163, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 1.1rem;
  }
`;

// New toggle components and other styled components
const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2rem auto;
  gap: 1rem;
`;

const PeriodOption = styled.span`
  font-size: 1rem;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? '#333' : '#999'};
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
`;

const ToggleSwitch = styled.div`
  width: 60px;
  height: 30px;
  background-color: ${props => props.isYearly ? 'var(--primary-color)' : '#e0e0e0'};
  border-radius: 15px;
  padding: 5px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.isYearly ? 'var(--primary-color)' : '#d0d0d0'};
  }
`;

const ToggleSlider = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: white;
  position: absolute;
  top: 5px;
  left: ${props => props.isYearly ? '35px' : '5px'};
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const SaveBadge = styled.span`
  background-color: #4caf50;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.8rem;
  margin-left: 8px;
`;

const NoPricesMessage = styled.div`
  text-align: center;
  color: #666;
  font-size: 1.2rem;
  padding: 2rem;
`;

const StyledSubscriptionPlans = styled.div`
  width: var(--section-width);
  margin: var(--section-margin) auto;

  .header {
    text-align: center;
    margin-bottom: 3rem;

    h1 {
      font-size: 2.5rem;
      color: #333;
      margin-bottom: 0.5rem;
    }

    p {
      font-size: 1.1rem;
      color: #666;
    }
    
    .active-coupon-section {
      background: linear-gradient(135deg, #e8f5e8 0%, #f0f8f0 100%);
      border-radius: 16px;
      padding: 25px;
      margin: 2rem auto;
      border: 2px solid #4caf50;
      max-width: 600px;
      position: relative;
      
      .coupon-header {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 20px;
        
        h3 {
          margin: 0;
          color: var(--text-color);
          font-size: 1.4rem;
          font-weight: 700;
        }
      }
      
      .coupon-card {
        background: white;
        border-radius: 12px;
        padding: 20px;
        border: 1px solid #e0e0e0;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
        position: relative;
        
        .coupon-info {
          h4 {
            margin: 0 0 8px 0;
            color: var(--text-color);
            font-size: 1.2rem;
            font-weight: 600;
          }
          
          p {
            margin: 0 0 15px 0;
            color: var(--text-gray-color);
            font-size: 1rem;
            line-height: 1.4;
          }
        }
        
        .remove-coupon-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          background: #f5f5f5;
          border: none;
          border-radius: 50%;
          width: 25px;
          height: 25px;
          font-size: 1.2rem;
          cursor: pointer;
          color: #666;
          transition: all 0.3s ease;
          
          &:hover {
            background: #e0e0e0;
            color: #333;
          }
        }
      }
      
      .coupon-instruction {
        margin: 15px 0 0 0;
        font-size: 0.9rem;
        color: #2e7d32;
        text-align: center;
        font-style: italic;
      }
    }
  }

  .plans-container {
    display: flex;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
    min-height: 40vh;

    @media (min-width: 992px) {
      flex-wrap: nowrap;
    }
  }
`;
